import React, { useEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Collapse,
  IconButton,
  Box,
} from "@mui/material";
import {
  HomeIcon,
  AnalyticsIcon,
  TransactionsIcon,
  VendingMachineIcon,
  InventoryIcon,
  AdvertisementIcon,
  MembershipIcon,
  DiscountIcon,
  ReservationIcon,
  ReturnIcon,
  CouponIcon,
  UserIcon,
  MaintenanceIcon,
  HelpIcon,
  TaxIcon,
  SpareIcon,
  ServiceIcon,
  ManageIcon,
  ViewIcon,
  FranchiseIcon,
  Academy,
} from "../CustomIcons";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
// import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from "react-router-dom";
import "./MobileSidebar.css";
import { ROUTES } from "../../../Utils/constants";
import { useThemeContext } from "../../../Context/ThemeContext";
import { useTranslation } from "react-i18next";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import useAuth from "../../../Hooks/useAuth";
import { getFranchiseMachineType } from "../../../Services/authService";

function MobileSidebar({ open, toggleSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [openView, setOpenView] = useState(false);
  const [openManage, setOpenManage] = useState(false);
  const [openMaintenance, setOpenMaintenance] = useState(false);
  const { mode } = useThemeContext();
  const { t } = useTranslation();
  const [machineType, setMachineType] = useState("NONE");

  // const isBoth = machineType === "BOTH";
  // const isReturnOnly = machineType === "RETURN_ONLY";
  // const isVendingOnly = machineType === "VENDING_ONLY";

  // const showVendingMachines = isBoth || isVendingOnly;
  // const showReturns = isBoth ;

  const isBoth = machineType === "BOTH";
  const isReturnOnly = machineType === "RETURN_ONLY";
  const isVendingOnly = machineType === "VENDING_ONLY";
  const isReturnFoodVending = machineType === "RETURN_AND_FOOD_VENDING";

  // Vending items show for BOTH / VENDING_ONLY / RETURN_FOOD_VENDING
  const showVendingMachines = isBoth || isVendingOnly || isReturnFoodVending;

  // Returns show ONLY for RETURN_FOOD_VENDING
  const showReturns = isReturnFoodVending;

  const showManageVendingMachines = machineType !== "NONE";
  const showSpares = !user?.roles?.includes("ROLE_SUPER_ADMIN");

  const maintenanceRoutes = [
    ROUTES.SERVICE_REQUEST,
    ROUTES.COMPLAINTS,
    ...(showSpares
      ? [ROUTES.SPARES, ROUTES.CARTPAGE, ROUTES.ORDERS, ROUTES.ORDER_DETAILS]
      : []),
  ];

  const sidebarBackground = mode === "dark" ? "#252525" : "#EFF3F6";
  const sidebarMenuBackground = mode === "dark" ? "#2D2D2D" : "#FFFFFF";
  const iconSidebarBackground = mode === "dark" ? "#1B1B1B" : "#ECEDF8";
  const blueColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

  // Expand/collapse submenus based on the active route
  React.useEffect(() => {
    if (
      location.pathname.includes(ROUTES.SALES_ANALYTICS) ||
      location.pathname.includes(ROUTES.TRANSACTIONS)
    ) {
      setOpenView(true);
    }
    if (
      location.pathname.includes(ROUTES.VENDING_MACHINES) ||
      location.pathname.includes(ROUTES.INVENTORY) ||
      location.pathname.includes(ROUTES.ADVERTISEMENTS) ||
      location.pathname.includes(ROUTES.MEMBERSHIP_CARDS) ||
      location.pathname.includes(ROUTES.DISCOUNTS) ||
      location.pathname.includes(ROUTES.COUPONS) ||
      location.pathname.includes(ROUTES.RESERVATIONS) ||
      location.pathname.includes(ROUTES.RETURNS) ||
      location.pathname.includes(ROUTES.USERS)
    ) {
      setOpenManage(true);
    }
    if (
      location.pathname.includes(ROUTES.SERVICE_REQUEST) ||
      location.pathname.includes(ROUTES.COMPLAINTS) ||
      location.pathname.includes(ROUTES.SPARES) ||
      location.pathname.includes(ROUTES.CARTPAGE) ||
      location.pathname.includes(ROUTES.ORDERS) ||
      location.pathname.includes(ROUTES.ORDER_DETAILS)
    ) {
      setOpenMaintenance(true);
    }
  }, [location]);

  // Fetch the franchise machine type (NONE / RETURN_ONLY / VENDING_ONLY / BOTH)
  React.useEffect(() => {
    const fetchMachineType = async () => {
      try {
        const response = await getFranchiseMachineType();
        setMachineType(response?.data?.machineType || "NONE");
      } catch (error) {
        console.error("Failed to fetch franchise machine type:", error);
        setMachineType("NONE");
      }
    };

    fetchMachineType();
  }, []);

  // Function to check if the route is active
  const isActive = (route) => location.pathname === route;
  const isParentActive = (routes) =>
    routes.some((route) => location.pathname.includes(route));

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={toggleSidebar}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: "200px",
          padding: "8px",
          backgroundColor: sidebarMenuBackground,
          marginTop: "4rem",
          marginLeft: "1rem",
          borderRadius: "6px",
          height: "auto",
        },
      }}
    >
      {/* <div className="sidebar-header">
                <IconButton onClick={toggleSidebar}>
                    <CloseIcon />
                </IconButton>
            </div> */}
      <List sx={{ paddingTop: "0px", paddingBottom: "0px" }}>
        {/* Home Section */}
        <ListItem
          className="sidebar-section-list sidebar-section-list5"
          sx={{ backgroundColor: sidebarBackground }}
          disablePadding
          onClick={() => {
            navigate(ROUTES.HOME);
            toggleSidebar();
          }}
        >
          <ListItemButton
            sx={{
              "&:hover": {
                backgroundColor: "transparent", // Disables the hover background color change
                opacity: 1, // Removes opacity changes on hover if applicable
              },
            }}
            className={`sidebar-section-listitembutton ${isActive(ROUTES.HOME) ? "active" : ""}`}
          >
            <ListItemIcon className="sidebar-navicons-iconroot">
              <HomeIcon color={isActive(ROUTES.HOME) ? blueColor : "#6F6F6F"} />
            </ListItemIcon>
            <h2
              className="sidebar-navitems-main"
              style={{
                color: isActive(ROUTES.HOME) ? blueColor : "", // Dynamically change the color
              }}
            >
              {t("sidebar.home")}
            </h2>
          </ListItemButton>
        </ListItem>

        {/* View Section */}
        {!user?.roles?.includes("ROLE_EMPLOYEE") && (
          <List
            sx={{ backgroundColor: sidebarBackground }}
            className={`sidebar-section-list sidebar-section-list5 ${isParentActive([ROUTES.SALES_ANALYTICS, ROUTES.TRANSACTIONS]) ? "active-parent" : ""}`}
          >
            <ListItem
              disablePadding
              onClick={() => {
                setOpenView(!openView);
              }}
            >
              <ListItemButton
                sx={{
                  "&:hover": {
                    backgroundColor: "transparent", // Disables the hover background color change
                    opacity: 1, // Removes opacity changes on hover if applicable
                  },
                }}
                className={`sidebar-section-listitembutton ${isParentActive([ROUTES.SALES_ANALYTICS, ROUTES.TRANSACTIONS]) ? "active-parent" : ""}`}
              >
                <ListItemIcon className="sidebar-navicons-iconroot">
                  <ViewIcon
                    color={
                      isParentActive([
                        ROUTES.SALES_ANALYTICS,
                        ROUTES.TRANSACTIONS,
                      ])
                        ? blueColor
                        : "#6F6F6F"
                    }
                  />
                </ListItemIcon>
                <h2 className="sidebar-navitems-main">{t("sidebar.view")}</h2>
                {openView ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openView} timeout="auto" unmountOnExit>
              <List component="div">
                <ListItem
                  disablePadding
                  onClick={() => {
                    navigate(ROUTES.SALES_ANALYTICS);
                    toggleSidebar();
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      "&:hover": {
                        backgroundColor: "transparent", // Disables the hover background color change
                        opacity: 1, // Removes opacity changes on hover if applicable
                      },
                    }}
                    className={`sidebar-section-sub ${isActive(ROUTES.SALES_ANALYTICS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{ background: iconSidebarBackground }}
                      className="sidebar-navicons-subiconroot subiconsec-active"
                    >
                      <Box
                        sx={{
                          background: isParentActive([ROUTES.SALES_ANALYTICS])
                            ? blueColor
                            : "transparent",
                          padding: "0px 6px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease", // Smooth transition effect
                          "&:hover": {
                            backgroundColor: isParentActive([
                              ROUTES.SALES_ANALYTICS,
                            ])
                              ? ""
                              : "#FFF", // Set background color to white on hover
                          },
                        }}
                      >
                        <AnalyticsIcon
                          color={
                            isParentActive([ROUTES.SALES_ANALYTICS])
                              ? "#FFF"
                              : "#6F6F6F"
                          }
                        />
                      </Box>
                    </ListItemIcon>
                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([ROUTES.SALES_ANALYTICS])
                          ? blueColor
                          : "", // Dynamically change the color
                      }}
                    >
                      {t("sidebar.sales_analytics")}
                    </h2>
                  </ListItemButton>
                </ListItem>

                <ListItem
                  disablePadding
                  onClick={() => {
                    navigate(ROUTES.TRANSACTIONS);
                    toggleSidebar();
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      "&:hover": {
                        backgroundColor: "transparent", // Disables the hover background color change
                        opacity: 1, // Removes opacity changes on hover if applicable
                      },
                    }}
                    className={`sidebar-section-sub ${isActive(ROUTES.TRANSACTIONS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{ background: iconSidebarBackground }}
                      className="sidebar-navicons-subiconroot subiconsec-active2"
                    >
                      <Box
                        sx={{
                          background: isParentActive([ROUTES.TRANSACTIONS])
                            ? blueColor
                            : "transparent",
                          padding: "2px 6px 0px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease", // Smooth transition effect
                          "&:hover": {
                            backgroundColor: isParentActive([
                              ROUTES.TRANSACTIONS,
                            ])
                              ? ""
                              : "#FFF", // Set background color to white on hover
                          },
                        }}
                      >
                        <TransactionsIcon
                          color={
                            isParentActive([ROUTES.TRANSACTIONS])
                              ? "#FFF"
                              : "#6F6F6F"
                          }
                        />
                      </Box>
                    </ListItemIcon>
                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([ROUTES.TRANSACTIONS])
                          ? blueColor
                          : "", // Dynamically change the color
                      }}
                    >
                      {t("sidebar.transactions")}
                    </h2>
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </List>
        )}

        {/* Manage Section */}
        <List
          sx={{ backgroundColor: sidebarBackground }}
          className={`sidebar-section-list sidebar-section-list5 ${isParentActive([ROUTES.VENDING_MACHINES, ROUTES.ADD_MACHINE, ROUTES.MACHINE_DETAILS, ROUTES.INVENTORY, ROUTES.ADVERTISEMENTS, ROUTES.ADD_ADVERTISEMENT, ROUTES.MEMBERSHIP_CARDS, ROUTES.DISCOUNTS, ROUTES.COUPONS, ROUTES.RESERVATIONS, ROUTES.RETURNS, ROUTES.USERS]) ? "active-parent" : ""}`}
        >
          <ListItem
            disablePadding
            onClick={() => {
              setOpenManage(!openManage);
            }}
          >
            <ListItemButton
              sx={{
                "&:hover": {
                  backgroundColor: "transparent", // Disables the hover background color change
                  opacity: 1, // Removes opacity changes on hover if applicable
                },
              }}
              className={`sidebar-section-listitembutton ${isParentActive([ROUTES.VENDING_MACHINES, ROUTES.ADD_MACHINE, ROUTES.MACHINE_DETAILS, ROUTES.INVENTORY, ROUTES.ADVERTISEMENTS, ROUTES.ADD_ADVERTISEMENT, ROUTES.MEMBERSHIP_CARDS, ROUTES.DISCOUNTS, ROUTES.COUPONS, ROUTES.RESERVATIONS, ROUTES.RETURNS, ROUTES.USERS]) ? "active-parent" : ""}`}
            >
              <ListItemIcon className="sidebar-navicons-iconroot">
                <ManageIcon
                  color={
                    isParentActive([
                      ROUTES.VENDING_MACHINES,
                      ROUTES.ADD_MACHINE,
                      ROUTES.MACHINE_DETAILS,
                      ROUTES.INVENTORY,
                      ROUTES.ADVERTISEMENTS,
                      ROUTES.ADD_ADVERTISEMENT,
                      ROUTES.MEMBERSHIP_CARDS,
                      ROUTES.DISCOUNTS,
                      ROUTES.COUPONS,
                      ROUTES.RESERVATIONS,
                      ROUTES.RETURNS,
                      ROUTES.USERS,
                    ])
                      ? blueColor
                      : "#6F6F6F"
                  }
                />
              </ListItemIcon>
              <h2 className="sidebar-navitems-main">{t("sidebar.manage")}</h2>
              {openManage ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={openManage} timeout="auto" unmountOnExit>
            <List component="div">
              {/* Vending Machines — shown for both machine types (VENDING_ONLY, RETURN_ONLY, BOTH) */}
              {showManageVendingMachines && (
                <ListItem
                  disablePadding
                  onClick={() => {
                    navigate(ROUTES.VENDING_MACHINES);
                    toggleSidebar();
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      "&:hover": {
                        backgroundColor: "transparent", // Disables the hover background color change
                        opacity: 1, // Removes opacity changes on hover if applicable
                      },
                    }}
                    className={`sidebar-section-sub ${isParentActive([ROUTES.VENDING_MACHINES, ROUTES.ADD_MACHINE, ROUTES.MACHINE_DETAILS]) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{ background: iconSidebarBackground }}
                      className="sidebar-navicons-subiconroot subiconsec-active"
                    >
                      <Box
                        sx={{
                          background: isParentActive([
                            ROUTES.VENDING_MACHINES,
                            ROUTES.ADD_MACHINE,
                            ROUTES.MACHINE_DETAILS,
                          ])
                            ? blueColor
                            : "transparent",
                          padding: "2px 6px 0px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease", // Smooth transition effect
                          "&:hover": {
                            backgroundColor: isParentActive([
                              ROUTES.VENDING_MACHINES,
                              ROUTES.ADD_MACHINE,
                              ROUTES.MACHINE_DETAILS,
                            ])
                              ? ""
                              : "#FFF", // Set background color to white on hover
                          },
                        }}
                      >
                        <VendingMachineIcon
                          color={
                            isParentActive([
                              ROUTES.VENDING_MACHINES,
                              ROUTES.ADD_MACHINE,
                              ROUTES.MACHINE_DETAILS,
                            ])
                              ? "#FFF"
                              : "#6F6F6F"
                          }
                        />
                      </Box>
                    </ListItemIcon>
                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([
                          ROUTES.VENDING_MACHINES,
                          ROUTES.ADD_MACHINE,
                          ROUTES.MACHINE_DETAILS,
                        ])
                          ? blueColor
                          : "", // Dynamically change the color
                      }}
                    >
                      {t("sidebar.vending_machines")}
                    </h2>
                  </ListItemButton>
                </ListItem>
              )}

              {/* Inventory — vending-related */}
              {showVendingMachines && (
                <ListItem
                  disablePadding
                  onClick={() => {
                    navigate(ROUTES.INVENTORY);
                    toggleSidebar();
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      "&:hover": {
                        backgroundColor: "transparent", // Disables the hover background color change
                        opacity: 1, // Removes opacity changes on hover if applicable
                      },
                    }}
                    className={`sidebar-section-sub ${isActive(ROUTES.INVENTORY) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{ background: iconSidebarBackground }}
                      className="sidebar-navicons-subiconroot"
                    >
                      <Box
                        sx={{
                          background: isParentActive([ROUTES.INVENTORY])
                            ? blueColor
                            : "transparent",
                          padding: "2px 6px 0px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease", // Smooth transition effect
                          "&:hover": {
                            backgroundColor: isParentActive([ROUTES.INVENTORY])
                              ? ""
                              : "#FFF", // Set background color to white on hover
                          },
                        }}
                      >
                        <InventoryIcon
                          color={
                            isParentActive([ROUTES.INVENTORY])
                              ? "#FFF"
                              : "#6F6F6F"
                          }
                        />
                      </Box>
                    </ListItemIcon>
                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([ROUTES.INVENTORY])
                          ? blueColor
                          : "", // Dynamically change the color
                      }}
                    >
                      {t("sidebar.inventory")}
                    </h2>
                  </ListItemButton>
                </ListItem>
              )}

              {/* Advertisements — shown for both machine types (VENDING_ONLY, RETURN_ONLY, BOTH) */}
              {showManageVendingMachines && (
                <ListItem
                  disablePadding
                  onClick={() => {
                    navigate(ROUTES.ADVERTISEMENTS);
                    toggleSidebar();
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      "&:hover": {
                        backgroundColor: "transparent", // Disables the hover background color change
                        opacity: 1, // Removes opacity changes on hover if applicable
                      },
                    }}
                    className={`sidebar-section-sub ${isParentActive([ROUTES.ADVERTISEMENTS, ROUTES.ADD_ADVERTISEMENT]) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{ background: iconSidebarBackground }}
                      className="sidebar-navicons-subiconroot"
                    >
                      <Box
                        sx={{
                          background: isParentActive([
                            ROUTES.ADVERTISEMENTS,
                            ROUTES.ADD_ADVERTISEMENT,
                          ])
                            ? blueColor
                            : "transparent",
                          padding: "2px 6px 0px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease", // Smooth transition effect
                          "&:hover": {
                            backgroundColor: isParentActive([
                              ROUTES.ADVERTISEMENTS,
                              ROUTES.ADD_ADVERTISEMENT,
                            ])
                              ? ""
                              : "#FFF", // Set background color to white on hover
                          },
                        }}
                      >
                        <AdvertisementIcon
                          color={
                            isParentActive([
                              ROUTES.ADVERTISEMENTS,
                              ROUTES.ADD_ADVERTISEMENT,
                            ])
                              ? "#FFF"
                              : "#6F6F6F"
                          }
                        />
                      </Box>
                    </ListItemIcon>
                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([
                          ROUTES.ADVERTISEMENTS,
                          ROUTES.ADD_ADVERTISEMENT,
                        ])
                          ? blueColor
                          : "", // Dynamically change the color
                      }}
                    >
                      {t("sidebar.advertisements")}
                    </h2>
                  </ListItemButton>
                </ListItem>
              )}

              {/* Membership Cards — vending-related, super-admin only */}
              {user?.roles?.includes("ROLE_SUPER_ADMIN") &&
                showVendingMachines && (
                  <ListItem
                    disablePadding
                    onClick={() => {
                      navigate(ROUTES.MEMBERSHIP_CARDS);
                      toggleSidebar();
                    }}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        "&:hover": {
                          backgroundColor: "transparent", // Disables the hover background color change
                          opacity: 1, // Removes opacity changes on hover if applicable
                        },
                      }}
                      className={`sidebar-section-sub ${isActive(ROUTES.MEMBERSHIP_CARDS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className="sidebar-navicons-subiconroot"
                      >
                        <Box
                          sx={{
                            background: isParentActive([
                              ROUTES.MEMBERSHIP_CARDS,
                            ])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease", // Smooth transition effect
                            "&:hover": {
                              backgroundColor: isParentActive([
                                ROUTES.MEMBERSHIP_CARDS,
                              ])
                                ? ""
                                : "#FFF", // Set background color to white on hover
                            },
                          }}
                        >
                          <MembershipIcon
                            color={
                              isParentActive([ROUTES.MEMBERSHIP_CARDS])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([ROUTES.MEMBERSHIP_CARDS])
                            ? blueColor
                            : "", // Dynamically change the color
                        }}
                      >
                        {t("membershipCards.customers")}
                      </h2>
                    </ListItemButton>
                  </ListItem>
                )}

              {/* Discounts — vending-related */}
              {showVendingMachines && (
                <ListItem
                  disablePadding
                  onClick={() => {
                    navigate(ROUTES.DISCOUNTS);
                    toggleSidebar();
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      "&:hover": {
                        backgroundColor: "transparent", // Disables the hover background color change
                        opacity: 1, // Removes opacity changes on hover if applicable
                      },
                    }}
                    className={`sidebar-section-sub ${isActive(ROUTES.DISCOUNTS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{ background: iconSidebarBackground }}
                      className="sidebar-navicons-subiconroot"
                    >
                      <Box
                        sx={{
                          background: isParentActive([ROUTES.DISCOUNTS])
                            ? blueColor
                            : "transparent",
                          padding: "2px 6px 0px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease", // Smooth transition effect
                          "&:hover": {
                            backgroundColor: isParentActive([ROUTES.DISCOUNTS])
                              ? ""
                              : "#FFF", // Set background color to white on hover
                          },
                        }}
                      >
                        <DiscountIcon
                          color={
                            isParentActive([ROUTES.DISCOUNTS])
                              ? "#FFF"
                              : "#6F6F6F"
                          }
                        />
                      </Box>
                    </ListItemIcon>
                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([ROUTES.DISCOUNTS])
                          ? blueColor
                          : "", // Dynamically change the color
                      }}
                    >
                      {t("sidebar.discounts")}
                    </h2>
                  </ListItemButton>
                </ListItem>
              )}

              {/* Coupons — vending-related */}
              {showVendingMachines && (
                <ListItem
                  disablePadding
                  onClick={() => {
                    navigate(ROUTES.COUPONS);
                    toggleSidebar();
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      "&:hover": {
                        backgroundColor: "transparent", // Disables the hover background color change
                        opacity: 1, // Removes opacity changes on hover if applicable
                      },
                    }}
                    className={`sidebar-section-sub ${isActive(ROUTES.COUPONS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{ background: iconSidebarBackground }}
                      className={`sidebar-navicons-subiconroot ${user?.roles?.includes("ROLE_EMPLOYEE") ? "subiconsec-active2" : ""}`}
                    >
                      <Box
                        sx={{
                          background: isParentActive([ROUTES.COUPONS])
                            ? blueColor
                            : "transparent",
                          padding: "2px 6px 0px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease", // Smooth transition effect
                          "&:hover": {
                            backgroundColor: isParentActive([ROUTES.COUPONS])
                              ? ""
                              : "#FFF", // Set background color to white on hover
                          },
                        }}
                      >
                        <CouponIcon
                          color={
                            isParentActive([ROUTES.COUPONS])
                              ? "#FFF"
                              : "#6F6F6F"
                          }
                        />
                      </Box>
                    </ListItemIcon>
                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([ROUTES.COUPONS])
                          ? blueColor
                          : "", // Dynamically change the color
                      }}
                    >
                      {t("sidebar.coupons")}
                    </h2>
                  </ListItemButton>
                </ListItem>
              )}

              {/* Reservations — vending-related, not for employees */}
              {/* {!user?.roles?.includes("ROLE_EMPLOYEE") &&  showVendingMachines && (
              <ListItem
                disablePadding
                onClick={() => {
                  navigate(ROUTES.RESERVATIONS);
                  toggleSidebar();
                }}
              >
                <ListItemButton
                  sx={{
                    pl: 4,
                    "&:hover": {
                      backgroundColor: "transparent",
                      opacity: 1,
                    },
                  }}
                  className={`sidebar-section-sub ${
                    isParentActive[(ROUTES.RESERVATIONS)] ? "active" : ""
                  } ${!open ? "active-sub" : ""}`}
                >
                  <ListItemIcon
                    sx={{ background: iconSidebarBackground }}
                    className={`sidebar-navicons-subiconroot ${
                      user?.roles?.includes("ROLE_EMPLOYEE")
                        ? "subiconsec-active2"
                        : ""
                    }`}
                  >
                    <Box
                      sx={{
                        background: isParentActive([ROUTES.RESERVATIONS])
                          ? blueColor
                          : "transparent",
                        padding: "2px 6px 0px",
                        borderRadius: "6px",
                        transition: "background-color 0.3s ease",
                        "&:hover": {
                          backgroundColor: isParentActive([ROUTES.RESERVATIONS])
                            ? ""
                            : "#FFF",
                        },
                      }}
                    >
                      <ReservationIcon
                        color={
                          isActive(ROUTES.RESERVATIONS) ? "#FFF" : "#6F6F6F"
                        }
                      />
                    </Box>
                  </ListItemIcon>

                  <h2
                    className="sidebar-navitems-submain"
                    style={{
                      color: isParentActive([ROUTES.RESERVATIONS])
                        ? blueColor
                        : "",
                    }}
                  >
                    {t("sidebar.reservations")}
                  </h2>
                </ListItemButton>
              </ListItem>

              )} */}
              {/* Returns — return-related, not for employees */}
              {!user?.roles?.includes("ROLE_EMPLOYEE") && showReturns && (
                <ListItem
                  disablePadding
                  onClick={() => {
                    navigate(ROUTES.RETURNS);
                    toggleSidebar();
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 1,
                      },
                    }}
                    className={`sidebar-section-sub ${
                      isParentActive([ROUTES.RETURNS]) ? "active" : ""
                    } ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{ background: iconSidebarBackground }}
                      className={`sidebar-navicons-subiconroot ${
                        user?.roles?.includes("ROLE_EMPLOYEE")
                          ? "subiconsec-active2"
                          : ""
                      }`}
                    >
                      <Box
                        sx={{
                          background: isParentActive([ROUTES.RETURNS])
                            ? blueColor
                            : "transparent",
                          padding: "2px 6px 0px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease",
                          "&:hover": {
                            backgroundColor: isParentActive([ROUTES.RETURNS])
                              ? ""
                              : "#FFF",
                          },
                        }}
                      >
                        <ReturnIcon
                          color={isActive(ROUTES.RETURNS) ? "#FFF" : "#6F6F6F"}
                        />
                      </Box>
                    </ListItemIcon>

                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([ROUTES.RETURNS])
                          ? blueColor
                          : "",
                      }}
                    >
                      {t("sidebar.returns")}
                    </h2>
                  </ListItemButton>
                </ListItem>
              )}
              {!user?.roles?.includes("ROLE_EMPLOYEE") && (
                <ListItem
                  disablePadding
                  onClick={() => {
                    navigate(ROUTES.USERS);
                    toggleSidebar();
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      "&:hover": {
                        backgroundColor: "transparent", // Disables the hover background color change
                        opacity: 1, // Removes opacity changes on hover if applicable
                      },
                    }}
                    className={`sidebar-section-sub ${isActive(ROUTES.USERS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{ background: iconSidebarBackground }}
                      className="sidebar-navicons-subiconroot subiconsec-active2"
                    >
                      <Box
                        sx={{
                          background: isParentActive([ROUTES.USERS])
                            ? blueColor
                            : "transparent",
                          padding: "2px 6px 0px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease", // Smooth transition effect
                          "&:hover": {
                            backgroundColor: isParentActive([ROUTES.USERS])
                              ? ""
                              : "#FFF", // Set background color to white on hover
                          },
                        }}
                      >
                        <UserIcon
                          color={
                            isParentActive([ROUTES.USERS]) ? "#FFF" : "#6F6F6F"
                          }
                        />
                      </Box>
                    </ListItemIcon>
                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([ROUTES.USERS]) ? blueColor : "", // Dynamically change the color
                      }}
                    >
                      {t("sidebar.users")}
                    </h2>
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Collapse>
        </List>

        {/* Taxes Section */}

        {!user?.roles?.includes("ROLE_EMPLOYEE") && showVendingMachines && (
          <ListItem
            className="sidebar-section-list sidebar-section-list5"
            sx={{ backgroundColor: sidebarBackground }}
            disablePadding
            onClick={() => {
              navigate(ROUTES.TAXES);
              toggleSidebar();
            }}
          >
            <ListItemButton
              sx={{
                "&:hover": {
                  backgroundColor: "transparent", // Disables the hover background color change
                  opacity: 1, // Removes opacity changes on hover if applicable
                },
              }}
              className={`sidebar-section-listitembutton ${isActive(ROUTES.TAXES) ? "active" : ""}`}
            >
              <ListItemIcon className="sidebar-navicons-iconroot">
                <TaxIcon
                  color={isParentActive([ROUTES.TAXES]) ? blueColor : "#6F6F6F"}
                />
              </ListItemIcon>
              <h2
                className="sidebar-navitems-main"
                style={{
                  color: isParentActive([ROUTES.TAXES]) ? blueColor : "", // Dynamically change the color
                }}
              >
                {t("sidebar.taxes")}
              </h2>
            </ListItemButton>
          </ListItem>
        )}

        {/* Maintenance Section */}
        <List
          sx={{ backgroundColor: sidebarBackground }}
          className={`sidebar-section-list sidebar-section-list5 ${isParentActive([ROUTES.SERVICE_REQUEST, ROUTES.COMPLAINTS, ROUTES.SPARES, ROUTES.CARTPAGE, ROUTES.ORDERS, ROUTES.ORDER_DETAILS]) ? "active-parent" : ""}`}
        >
          <ListItem
            disablePadding
            onClick={() => {
              setOpenMaintenance(!openMaintenance);
            }}
          >
            <ListItemButton
              sx={{
                "&:hover": {
                  backgroundColor: "transparent", // Disables the hover background color change
                  opacity: 1, // Removes opacity changes on hover if applicable
                },
              }}
              className={`sidebar-section-listitembutton ${isParentActive([ROUTES.SERVICE_REQUEST, ROUTES.COMPLAINTS, ROUTES.SPARES, ROUTES.CARTPAGE, ROUTES.ORDERS, ROUTES.ORDER_DETAILS]) ? "active-parent" : ""}`}
            >
              <ListItemIcon className="sidebar-navicons-iconroot">
                <MaintenanceIcon
                  color={
                    isParentActive([
                      ROUTES.SERVICE_REQUEST,
                      ROUTES.COMPLAINTS,
                      ROUTES.SPARES,
                      ROUTES.CARTPAGE,
                      ROUTES.ORDERS,
                      ROUTES.ORDER_DETAILS,
                    ])
                      ? blueColor
                      : "#6F6F6F"
                  }
                />
              </ListItemIcon>
              <h2 className="sidebar-navitems-main">
                {t("sidebar.maintenance")}
              </h2>
              {openMaintenance ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={openMaintenance} timeout="auto" unmountOnExit>
            <List component="div">
              <ListItem
                disablePadding
                onClick={() => {
                  navigate(ROUTES.SERVICE_REQUEST);
                  toggleSidebar();
                }}
              >
                <ListItemButton
                  sx={{
                    pl: 4,
                    "&:hover": {
                      backgroundColor: "transparent", // Disables the hover background color change
                      opacity: 1, // Removes opacity changes on hover if applicable
                    },
                  }}
                  className={`sidebar-section-sub ${isParentActive([ROUTES.SERVICE_REQUEST, ROUTES.COMPLAINTS]) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                >
                  <ListItemIcon
                    sx={{
                      background: iconSidebarBackground,
                      borderRadius: "6px",
                      overflow: "hidden",
                    }}
                    className="sidebar-navicons-subiconroot subiconsec-active"
                  >
                    <Box
                      sx={{
                        background: isParentActive([
                          ROUTES.SERVICE_REQUEST,
                          ROUTES.COMPLAINTS,
                        ])
                          ? blueColor
                          : "transparent",
                        padding: "2px 6px 0px",
                        borderRadius: "6px",
                        transition: "background-color 0.3s ease", // Smooth transition effect
                        "&:hover": {
                          backgroundColor: isParentActive([
                            ROUTES.SERVICE_REQUEST,
                            ROUTES.COMPLAINTS,
                          ])
                            ? ""
                            : "#FFF", // Set background color to white on hover
                        },
                      }}
                    >
                      <ServiceIcon
                        color={
                          isParentActive([
                            ROUTES.SERVICE_REQUEST,
                            ROUTES.COMPLAINTS,
                          ])
                            ? "#FFF"
                            : "#6F6F6F"
                        }
                      />
                    </Box>
                  </ListItemIcon>
                  <h2
                    className="sidebar-navitems-submain"
                    style={{
                      color: isParentActive([
                        ROUTES.SERVICE_REQUEST,
                        ROUTES.COMPLAINTS,
                      ])
                        ? blueColor
                        : "", // Dynamically change the color
                    }}
                  >
                    {t("sidebar.service_request")}
                  </h2>
                </ListItemButton>
              </ListItem>

              {/* <ListItem
                disablePadding
                onClick={() => {
                  navigate(ROUTES.SPARES);
                  toggleSidebar();
                }}
              >
                <ListItemButton
                  sx={{
                    pl: 4,
                    "&:hover": {
                      backgroundColor: "transparent", // Disables the hover background color change
                      opacity: 1, // Removes opacity changes on hover if applicable
                    },
                  }}
                  className={`sidebar-section-sub ${isParentActive([ROUTES.SPARES, ROUTES.CARTPAGE, ROUTES.ORDERS, ROUTES.ORDER_DETAILS]) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                >
                  <ListItemIcon
                    sx={{ background: iconSidebarBackground }}
                    className="sidebar-navicons-subiconroot subiconsec-active2"
                  >
                    <Box
                      sx={{
                        background: isParentActive([
                          ROUTES.SPARES,
                          ROUTES.CARTPAGE,
                          ROUTES.ORDERS,
                          ROUTES.ORDER_DETAILS,
                        ])
                          ? blueColor
                          : "transparent",
                        padding: "2px 6px 0px",
                        borderRadius: "6px",
                        transition: "background-color 0.3s ease", // Smooth transition effect
                        "&:hover": {
                          backgroundColor: isParentActive([
                            ROUTES.SPARES,
                            ROUTES.CARTPAGE,
                            ROUTES.ORDERS,
                            ROUTES.ORDER_DETAILS,
                          ])
                            ? ""
                            : "#FFF", // Set background color to white on hover
                        },
                      }}
                    >
                      <SpareIcon
                        color={
                          isParentActive([
                            ROUTES.SPARES,
                            ROUTES.CARTPAGE,
                            ROUTES.ORDERS,
                            ROUTES.ORDER_DETAILS,
                          ])
                            ? "#FFF"
                            : "#6F6F6F"
                        }
                      />
                    </Box>
                  </ListItemIcon>
                  <h2
                    className="sidebar-navitems-submain"
                    style={{
                      color: isParentActive([
                        ROUTES.SPARES,
                        ROUTES.CARTPAGE,
                        ROUTES.ORDERS,
                        ROUTES.ORDER_DETAILS,
                      ])
                        ? blueColor
                        : "", // Dynamically change the color
                    }}
                  >
                    {t("sidebar.spares")}
                  </h2>
                </ListItemButton>
              </ListItem> */}
            </List>
          </Collapse>
        </List>

        {/* Franchise Section */}
        {user?.roles?.includes("ROLE_SUPER_ADMIN") && (
          <ListItem
            className="sidebar-section-list sidebar-section-list5"
            sx={{ backgroundColor: sidebarBackground }}
            disablePadding
            onClick={() => {
              navigate(ROUTES.FRANCHISE);
              toggleSidebar();
            }}
          >
            <ListItemButton
              sx={{
                pl: 4,
                "&:hover": {
                  backgroundColor: "transparent", // Disables the hover background color change
                  opacity: 1, // Removes opacity changes on hover if applicable
                },
              }}
              className={`sidebar-section-listitembutton ${isParentActive([ROUTES.FRANCHISE, ROUTES.ADDFRANCHISE, ROUTES.PAYOUT]) ? "active" : ""}`}
            >
              <ListItemIcon className="sidebar-navicons-iconroot">
                <FranchiseIcon
                  color={
                    isParentActive([
                      ROUTES.FRANCHISE,
                      ROUTES.ADDFRANCHISE,
                      ROUTES.PAYOUT,
                    ])
                      ? blueColor
                      : "#6F6F6F"
                  }
                />
              </ListItemIcon>
              <h2
                className="sidebar-navitems-main"
                style={{
                  color: isParentActive([
                    ROUTES.FRANCHISE,
                    ROUTES.ADDFRANCHISE,
                    ROUTES.PAYOUT,
                  ])
                    ? blueColor
                    : "", // Dynamically change the color
                }}
              >
                {t("sidebar.franchise")}
              </h2>
            </ListItemButton>
          </ListItem>
        )}

        {/* Academy Section */}
        <ListItem
          className="sidebar-section-list sidebar-section-list5"
          sx={{ backgroundColor: sidebarBackground }}
          disablePadding
          onClick={() => {
            navigate(ROUTES.ACADEMY);
            toggleSidebar();
          }}
        >
          <ListItemButton
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
                opacity: 1,
              },
            }}
            className={`sidebar-section-listitembutton ${isActive(ROUTES.ACADEMY) ? "active" : ""}`}
          >
            <ListItemIcon className="sidebar-navicons-iconroot">
              <Academy
                color={isActive(ROUTES.ACADEMY) ? blueColor : "#6F6F6F"}
              />
            </ListItemIcon>
            <h2
              className="sidebar-navitems-main"
              style={{
                color: isActive(ROUTES.ACADEMY) ? blueColor : "",
              }}
            >
              {t("sidebar.academy")}
            </h2>
          </ListItemButton>
        </ListItem>

        {/* Help Section */}
        <ListItem
          className="sidebar-section-list sidebar-section-list5"
          sx={{ backgroundColor: sidebarBackground }}
          disablePadding
          onClick={() => {
            navigate(ROUTES.HELP);
            toggleSidebar();
          }}
        >
          <ListItemButton
            sx={{
              pl: 4,
              "&:hover": {
                backgroundColor: "transparent", // Disables the hover background color change
                opacity: 1, // Removes opacity changes on hover if applicable
              },
            }}
            className={`sidebar-section-listitembutton ${isActive(ROUTES.HELP) ? "active" : ""}`}
          >
            <ListItemIcon className="sidebar-navicons-iconroot">
              <HelpIcon
                color={isParentActive([ROUTES.HELP]) ? blueColor : "#6F6F6F"}
              />
            </ListItemIcon>
            <h2
              className="sidebar-navitems-main"
              style={{
                color: isParentActive([ROUTES.HELP]) ? blueColor : "", // Dynamically change the color
              }}
            >
              {t("sidebar.help")}
            </h2>
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
}

export default MobileSidebar;
